/**
 * The signed-in account: profile and last known position.
 *
 * `/users/me` is the single source of truth for the profile. The MobX version
 * cached it in AuthStore.profile, ProfileStore.profile and sessionStorage
 * simultaneously, and the three could and did disagree; here there is one cache
 * entry and every screen reads it.
 */

import { api } from "../api";
import type {
    UpdateLocationRequest,
    UpdateProfileRequest,
    User,
    UserLocation,
} from "../types";
import { userLoaded, sessionCleared } from "../../store/slices/authSlice";

export const usersApi = api.injectEndpoints({
    endpoints: (build) => ({
        me: build.query<User, void>({
            query: () => "users/me",
            providesTags: ["User"],
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    // Mirrored into the auth slice so a reload can render a
                    // name before the first request completes.
                    dispatch(userLoaded(data));
                } catch {
                    // Reported to the caller through the hook's error state.
                }
            },
        }),

        updateMe: build.mutation<User, UpdateProfileRequest>({
            query: (body) => ({ url: "users/me", method: "PUT", body }),
            invalidatesTags: ["User"],
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(userLoaded(data));
                } catch {
                    // Reported to the caller through the hook's error state.
                }
            },
        }),

        /** Irreversible. The API erases the account; there is no undo path. */
        deleteMe: build.mutation<null, void>({
            query: () => ({ url: "users/me", method: "DELETE" }),
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                // Only on success: a failed delete must not sign the person
                // out of an account that still exists.
                try {
                    await queryFulfilled;
                } catch {
                    return;
                }
                dispatch(sessionCleared());
                dispatch(api.util.resetApiState());
            },
        }),

        myLocation: build.query<UserLocation, void>({
            query: () => "users/me/location",
            providesTags: ["Location"],
        }),

        /** Longitude first, as everywhere else in this contract. */
        updateLocation: build.mutation<UserLocation, UpdateLocationRequest>({
            query: (body) => ({ url: "users/me/location", method: "PUT", body }),
            invalidatesTags: ["Location"],
        }),

        // ── Profile photo ───────────────────────────────────────────────────

        /**
         * Replaces the account's photo.
         *
         * Multipart, under the field name `photo`, which is the only name the
         * API reads. The FormData is handed to fetch untouched: setting
         * Content-Type here would omit the multipart boundary the browser
         * generates and the request would arrive unparseable.
         *
         * The updated user is pushed into the auth slice on success, the same
         * way updateMe does, so the header avatar changes without a refetch.
         */
        uploadPhoto: build.mutation<User, File>({
            query: (file) => {
                const form = new FormData();
                form.append("photo", file);
                return { url: "users/me/photo", method: "POST", body: form };
            },
            invalidatesTags: ["User"],
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(userLoaded(data));
                } catch {
                    // Reported to the caller through the hook's error state.
                }
            },
        }),

        deletePhoto: build.mutation<User, void>({
            query: () => ({ url: "users/me/photo", method: "DELETE" }),
            invalidatesTags: ["User"],
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(userLoaded(data));
                } catch {
                    // Reported to the caller through the hook's error state.
                }
            },
        }),
    }),
});

export const {
    useMeQuery,
    useLazyMeQuery,
    useUpdateMeMutation,
    useDeleteMeMutation,
    useMyLocationQuery,
    useUpdateLocationMutation,
    useUploadPhotoMutation,
    useDeletePhotoMutation,
} = usersApi;
